import { useCallback, useState } from 'react';

type MatrixMapType = Map<string, boolean>;
type PendingChangesType = Record<
	number,
	Record<number, { initialState: boolean; checked: boolean }>
>;

export interface MappingState {
	matrix: MatrixMapType;
	blockMatrix: MatrixMapType;
}

export interface PendingChangesState {
	matrix: PendingChangesType;
	blockMatrix: PendingChangesType;
}

export const useMappingState = () => {
	const [modifiedCheckboxes, setModifiedCheckboxes] = useState<MappingState>({
		matrix: new Map(),
		blockMatrix: new Map(),
	});

	const [pendingChanges, setPendingChanges] = useState<PendingChangesState>({
		matrix: {},
		blockMatrix: {},
	});

	const handleMappingChange = useCallback(
		(
			trainingId: number,
			positionId: number,
			checked: boolean,
			isBlock = false,
		) => {
			const key = `${positionId}-${trainingId}`;
			const mapKey = isBlock ? 'blockMatrix' : 'matrix';

			// Use functional updates to avoid race conditions
			setModifiedCheckboxes((prevState) => {
				const newMaps = {
					matrix: new Map(prevState.matrix),
					blockMatrix: new Map(prevState.blockMatrix),
				};

				newMaps[mapKey].set(key, checked);

				return newMaps;
			});

			// Update pendingChanges with functional update
			setPendingChanges((prevState) => {
				const newPendingChanges = {
					matrix: { ...prevState.matrix },
					blockMatrix: { ...prevState.blockMatrix },
				};

				const changesKey = isBlock ? 'blockMatrix' : 'matrix';
				const updatedChanges = { ...prevState[changesKey] };

				// Create or update the position and training entries
				if (!updatedChanges[positionId]) {
					updatedChanges[positionId] = {};
				}

				// Track the initial state if this is a new change
				if (!updatedChanges[positionId][trainingId]) {
					// Determine the initial state from either the existing map or the original data
					const initialState = isBlock
						? (prevState.blockMatrix[positionId]?.[trainingId]?.initialState ??
							!checked)
						: (prevState.matrix[positionId]?.[trainingId]?.initialState ??
							!checked);

					updatedChanges[positionId][trainingId] = {
						initialState,
						checked,
					};
				} else {
					// Update the checked state for an existing change
					updatedChanges[positionId][trainingId].checked = checked;
				}

				// If the checked state matches the initial state, remove the change
				if (checked === updatedChanges[positionId][trainingId].initialState) {
					delete updatedChanges[positionId][trainingId];

					if (Object.keys(updatedChanges[positionId]).length === 0) {
						delete updatedChanges[positionId];
					}
				}

				newPendingChanges[changesKey] = updatedChanges;
				return newPendingChanges;
			});
		},
		[],
	);

	return {
		modifiedCheckboxes,
		pendingChanges,
		setModifiedCheckboxes,
		setPendingChanges,
		handleMappingChange,
	};
};
